import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAppState } from "../../context/AppStateContext";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { keys, useAvailableBands, useCatalog, useCustomerSearch, useRefreshAll, useSessions } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { isV2 } from "../../lib/config";
import { localName, money } from "../../lib/format";
import { useAutoPrint } from "../../hooks/usePrintReceipt";
import { onScan } from "../../lib/scanBus";
import type { CreatedTransaction, Guardian, Kid, SessionView } from "../../lib/types";
import { SectionHeader } from "../ui/Badges";
import { PaymentSection, usePaymentForm } from "../ui/PaymentSection";
import { WaiverDialog } from "../v2/WaiverDialog";
import type { WaiverStatus } from "../../lib/typesV2";
import { CouponPicker, couponReady, type CouponSelection } from "./CouponPicker";
import { useQueryClient } from "@tanstack/react-query";

const DEFAULT_RANKS = ["little_astronaut", "orbit_cadet", "cosmic_master"];

export function CheckInForm({ shiftOpen }: { shiftOpen: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const { prefill, clearPrefill } = useAppState();
  const refresh = useRefreshAll();
  const autoPrint = useAutoPrint();
  const qc = useQueryClient();
  const [birthDate, setBirthDate] = useState("");
  const catalog = useCatalog(isV2() && /^\d{4}-\d{2}-\d{2}$/.test(birthDate) ? birthDate : undefined);
  const bands = useAvailableBands();
  const form = usePaymentForm();
  // v2: post-paid (barcode wristband, billed from the exact time at the exit scan) is the branch default; pre-paid = a strict fixed time paid now
  const rules = useSessions().data?.rules;
  const [modeChoice, setModeChoice] = useState<"postpaid" | "prepaid" | null>(null);
  const mode: "postpaid" | "prepaid" = isV2() ? (modeChoice ?? rules?.defaultBillingMode ?? "postpaid") : "prepaid";
  const postpaid = mode === "postpaid";
  // ranks are the owner's list (Control Center); older servers use the built-in three
  const rankList = (rules?.ranks ?? DEFAULT_RANKS.map((code) => ({ code, name: "", nameAr: "" }))).map((r) => ({ code: r.code, label: r.name || r.nameAr ? (locale === "ar" ? r.nameAr : r.name) : t(`rank.${r.code}`) }));

  const [phone, setPhone] = useState("");
  const [parent, setParent] = useState("");
  const [kidName, setKidName] = useState("");
  const [rank, setRank] = useState<string>("orbit_cadet");
  const [passId, setPassId] = useState<string>("");
  const [addons, setAddons] = useState<Record<string, number>>({});
  const [band, setBand] = useState("");
  const [guardian, setGuardian] = useState<Guardian | null>(null);
  const [kid, setKid] = useState<Kid | null>(null);
  const [redeem, setRedeem] = useState(false);
  const [coupon, setCoupon] = useState<CouponSelection | null>(null);
  const { isManager } = useAuth();
  const [waLocale, setWaLocale] = useState(locale);
  const [busy, setBusy] = useState(false);
  /** v2: the family that must sign a waiver before this visit can start. */
  const [waiver, setWaiver] = useState<{ guardianId: string; kidId: string } | null>(null);

  const passes = useMemo(() => {
    const all = (catalog.data ?? []).filter((p) => p.kind === "pass" && p.sku !== "EXT-30").sort((a, b) => a.price - b.price);
    if (!postpaid) return all;
    // post-paid: one entry per ticket type (its first block); the bill follows the clock, not a chosen duration
    const first = new Map<string, (typeof all)[number]>();
    for (const p of all) {
      const key = p.id.split(":")[0]!;
      const cur = first.get(key);
      if (!cur || (p.durationMinutes ?? 0) < (cur.durationMinutes ?? 0)) first.set(key, p);
    }
    return [...first.values()];
  }, [catalog.data, postpaid]);
  const extras = useMemo(() => (catalog.data ?? []).filter((p) => p.kind === "addon" || p.kind === "fnb"), [catalog.data]);
  useEffect(() => {
    if ((!passId || !passes.some((p) => p.id === passId)) && passes[0]) setPassId(passes[0].id);
  }, [passes, passId]);
  useEffect(() => {
    setWaLocale(locale);
  }, [locale]);

  // Suggest the first free wristband.
  useEffect(() => {
    if (!band && bands.data?.[0]) setBand(bands.data[0]);
  }, [bands.data, band]);

  // Look up a returning family by phone.
  const digits = phone.replace(/\D/g, "");
  const search = useCustomerSearch(digits.length >= 8 ? phone : "");
  useEffect(() => {
    const hit = search.data?.find((g) => g.phone.replace(/\D/g, "").endsWith(digits.slice(-9)));
    if (hit && hit.id !== guardian?.id) {
      setGuardian(hit);
      setParent(hit.fullName);
      setWaLocale(hit.locale);
      if (hit.kids.length === 1) pickKid(hit.kids[0]!);
    }
    if (!hit && guardian && digits.length < 8) setGuardian(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.data, digits]);

  // Hand-off from the Loyalty tab.
  useEffect(() => {
    if (!prefill) return;
    setPhone(prefill.guardian.phone);
    setParent(prefill.guardian.fullName);
    setKidName(prefill.kid.name);
    setRedeem(!!prefill.redeem);
    void qc.fetchQuery({ queryKey: keys.search(prefill.guardian.phone), queryFn: () => api.get<{ guardians: Guardian[] }>(`/customers/search?q=${encodeURIComponent(prefill.guardian.phone)}`) }).then((r) => {
      const g = r.guardians.find((x) => x.id === prefill.guardian.id);
      if (g) {
        setGuardian(g);
        const k = g.kids.find((x) => x.id === prefill.kid.id);
        if (k) setKid(k);
      }
    });
    clearPrefill();
  }, [prefill, clearPrefill, qc]);

  // A scanned wristband that is free goes into the band field.
  useEffect(() => {
    return onScan((code) => {
      if (bands.data?.some((b) => b.toLowerCase() === code.trim().toLowerCase())) setBand(code.trim().toUpperCase());
    });
  }, [bands.data]);

  function pickKid(k: Kid) {
    setKid(k);
    setKidName(k.name);
    if (k.rank && rankList.some((r) => r.code === k.rank)) setRank(k.rank);
  }

  const pass = passes.find((p) => p.id === passId);
  const extrasTotal = extras.reduce((s, p) => s + (addons[p.id] ?? 0) * p.price, 0);
  const free = !postpaid && redeem && !!pass;
  const couponAmount = free || postpaid ? 0 : (coupon?.amount ?? 0);
  // post-paid: nothing is collected at the door; the bill is computed at the exit scan
  const total = postpaid ? 0 : Math.max(0, (pass?.price ?? 0) + extrasTotal - (free ? pass!.price : 0) - couponAmount);
  const addonList = useMemo(() => Object.entries(addons).filter(([, q]) => q > 0).map(([productId, qty]) => ({ productId, qty })), [addons]);
  // The customer the server will see: a chosen kid, or a returning kid whose name was typed.
  const evalKidId = kid?.id ?? guardian?.kids.find((k) => k.name.toLowerCase() === kidName.trim().toLowerCase())?.id ?? null;
  useEffect(() => {
    if (free) setCoupon(null); // a free stamp pass cannot be combined with a coupon
  }, [free]);
  const need = rules?.stampsNeeded ?? 6;
  const canRedeem = need > 0 && (kid?.stamps ?? 0) >= need;
  const creditOk = !!guardian && guardian.creditLimit - guardian.outstandingCredit >= total && total > 0;
  const methods = creditOk ? (["cash", "card", "cliq", "credit"] as const) : (["cash", "card", "cliq"] as const);

  function reset() {
    setPhone(""); setParent(""); setKidName(""); setBirthDate(""); setKid(null); setGuardian(null); setRedeem(false); setAddons({}); setBand(""); setCoupon(null);
    form.reset();
    void qc.invalidateQueries({ queryKey: keys.bands });
  }

  /** v2: register the family and child first, so a waiver can be signed against real ids before any money moves. */
  async function resolveIds(): Promise<{ guardianId: string; kidId: string }> {
    let gid = guardian?.id;
    if (!gid) gid = (await api.post<{ guardian: { id: string } }>("/customers/guardians", { fullName: parent.trim(), phone, locale: waLocale })).guardian.id;
    let kidId = kid && kid.name === kidName.trim() ? kid.id : undefined;
    if (!kidId) kidId = (await api.post<{ kid: { id: string } }>(`/customers/guardians/${gid}/kids`, { name: kidName.trim(), birthDate: birthDate || null })).kid.id;
    return { guardianId: gid, kidId };
  }

  async function send(ids?: { guardianId: string; kidId: string }) {
    if (!pass) return;
    const res = await api.post<{ session: SessionView; transaction: CreatedTransaction | null }>("/sessions/check-in", {
      guardian: ids ? { id: ids.guardianId } : guardian ? { id: guardian.id } : { fullName: parent.trim(), phone, locale: waLocale },
      kid: ids ? { id: ids.kidId } : kid && kid.name === kidName.trim() ? { id: kid.id } : { name: kidName.trim(), rank },
      wristbandTag: band.trim().toUpperCase(),
      passProductId: pass.id,
      addons: postpaid ? [] : Object.entries(addons).filter(([, q]) => q > 0).map(([productId, qty]) => ({ productId, qty })),
      payments: postpaid ? [] : form.payments(total),
      redeemStamps: free,
      billingMode: isV2() ? mode : undefined,
      coupon: coupon && !postpaid
        ? {
            code: coupon.rule.code,
            note: coupon.note.trim() || undefined,
            approval: coupon.rule.requiresApproval && !isManager && !isV2() ? { username: coupon.approvalUser.trim(), pin: coupon.approvalPin } : undefined,
            overrideId: coupon.overrideId,
          }
        : undefined,
    });
    toast(t("checkin.done", { name: res.session.kid.name, band: res.session.wristbandTag }), "success");
    if (res.transaction) autoPrint(res.transaction.id, res.session.wristbandTag);
    refresh();
    reset();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!pass) return;
    setBusy(true);
    try {
      if (isV2()) {
        const ids = await resolveIds();
        const ws = await api.get<WaiverStatus>(`/waivers/status?kidId=${ids.kidId}&productId=${pass.id.split(":")[0]}`);
        if (!ws.canStart) {
          setWaiver(ids); // the signature pad opens; check-in continues when it is signed
          return;
        }
        await send(ids);
      } else {
        await send();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  }

  async function afterSigned() {
    const ids = waiver;
    setWaiver(null);
    if (!ids) return;
    setBusy(true);
    try {
      await send(ids);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  }

  const ready = (shiftOpen || postpaid) && !!pass && !!band && kidName.trim().length > 0 && parent.trim().length >= 2 && digits.length >= 8 && form.isValid(total) && couponReady(coupon, isManager);

  return (
    <form onSubmit={submit} className="card space-y-3.5 p-4 sm:p-5" aria-label={t("checkin.title")}>
      <SectionHeader title={`⚡ ${t("checkin.title")}`} right={<span className="rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-cosmic-purple">{t("checkin.terminal")}</span>} />

      {isV2() && (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t("checkin.mode.label")}>
            {(["postpaid", "prepaid"] as const).map((m) => (
              <button key={m} type="button" role="radio" data-mode={m} aria-checked={mode === m} onClick={() => setModeChoice(m)}
                className={`rounded-xl border-2 px-3 py-1.5 text-xs font-black transition ${mode === m ? "border-cosmic-purple bg-purple-50 text-cosmic-purple" : "border-gray-200 bg-white text-gray-600 hover:border-cosmic-purple"}`}>
                {m === "postpaid" ? "🧾" : "⏳"} {t(`checkin.mode.${m}`)}
              </button>
            ))}
          </div>
          <p className="text-[11px] font-semibold text-gray-500">{t(`checkin.mode.${mode}Hint`)}</p>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-3 ${isV2() ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}>
        <div>
          <label className="label" htmlFor="ci-phone">{t("checkin.parentPhone")}</label>
          <input id="ci-phone" className="field num font-mono" type="tel" inputMode="tel" value={phone} onChange={(e) => { setPhone(e.target.value); if (guardian) { setGuardian(null); setKid(null); } }} required placeholder="07X XXX XXXX" />
        </div>
        <div>
          <label className="label" htmlFor="ci-parent">{t("checkin.parentName")}</label>
          <input id="ci-parent" className="field" value={parent} onChange={(e) => setParent(e.target.value)} required disabled={!!guardian} />
        </div>
        <div>
          <label className="label" htmlFor="ci-kid">{t("checkin.kidName")}</label>
          <input id="ci-kid" className="field" value={kidName} onChange={(e) => { setKidName(e.target.value); if (kid && kid.name !== e.target.value.trim()) setKid(null); }} required />
        </div>
        <div>
          <label className="label" htmlFor="ci-rank">{t("checkin.rank")}</label>
          <select id="ci-rank" className="field" value={rank} onChange={(e) => setRank(e.target.value)}>
            {rankList.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </div>
        {isV2() && (
          <div>
            <label className="label" htmlFor="ci-birth">{t("checkin.birthDate")}</label>
            <input id="ci-birth" className="field num" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
          </div>
        )}
      </div>

      {guardian && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-2.5 text-xs font-bold" role="status">
          <span className="text-emerald-700">✓ {t("checkin.returning")}</span>
          {guardian.kids.map((k) => (
            <button key={k.id} type="button" onClick={() => pickKid(k)} className={`rounded-full border px-2.5 py-1 ${kid?.id === k.id ? "border-cosmic-purple bg-cosmic-purple text-white" : "border-gray-200 bg-white"}`}>
              {k.name} <span className="num">⭐{k.stamps}</span>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-12">
        <div className="sm:col-span-5">
          <span className="label">{postpaid ? t("checkin.ticketType") : t("checkin.duration")}</span>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={t("checkin.duration")}>
            {passes.map((p) => (
              <button key={p.id} type="button" role="radio" aria-checked={passId === p.id} onClick={() => setPassId(p.id)}
                className={`flex flex-col items-center rounded-xl border-2 p-2 text-center ${passId === p.id ? "border-cosmic-purple bg-purple-50/50" : "border-gray-200 bg-white hover:border-cosmic-purple"}`}>
                <span className={`text-xs font-black ${passId === p.id ? "text-cosmic-purple" : "text-gray-700"}`}>{localName(p, locale)}</span>
                <span className="num font-mono text-[10px] font-extrabold text-gray-600">{money(p.price)} {t("currency")}</span>
              </button>
            ))}
          </div>
        </div>
        <div className={`sm:col-span-4 ${postpaid ? "hidden" : ""}`}>
          <span className="label">{t("checkin.extras")}</span>
          <div className="space-y-1.5">
            {extras.slice(0, 4).map((p) => {
              const qty = addons[p.id] ?? 0;
              const out = p.tracksStock && (p.qtyOnHand ?? 0) <= 0;
              return (
                <div key={p.id} className="flex items-center justify-between gap-2 text-xs font-bold text-gray-700">
                  <span className={out ? "text-gray-300 line-through" : ""}>{localName(p, locale)} <span className="num text-gray-400">+{money(p.price)}</span></span>
                  <span className="flex items-center gap-1">
                    <button type="button" className="btn-ghost !px-2 !py-0.5" disabled={qty === 0} onClick={() => setAddons((a) => ({ ...a, [p.id]: Math.max(0, qty - 1) }))} aria-label={`− ${localName(p, locale)}`}>−</button>
                    <span className="num w-4 text-center">{qty}</span>
                    <button type="button" className="btn-ghost !px-2 !py-0.5" disabled={out} onClick={() => setAddons((a) => ({ ...a, [p.id]: qty + 1 }))} aria-label={`+ ${localName(p, locale)}`}>+</button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="space-y-2 rounded-xl border border-purple-200 bg-purple-50/60 p-3 sm:col-span-3">
          <div className="flex items-center justify-between gap-2 text-xs">
            <label className="font-bold text-gray-600" htmlFor="ci-band">{t("checkin.tag")}</label>
            <div className="flex items-center gap-1">
              <input id="ci-band" className="num w-20 rounded border border-purple-300 bg-white px-1 py-0.5 text-center font-mono text-xs font-black" value={band} onChange={(e) => setBand(e.target.value.toUpperCase())} />
              <button type="button" className="btn-ghost !px-1.5 !py-0.5" title={t("checkin.nextBand")} aria-label={t("checkin.nextBand")} onClick={() => setBand(bands.data?.find((b) => b !== band) ?? band)}>↻</button>
            </div>
          </div>
          {postpaid ? (
            <div className="text-[11px] font-black text-cosmic-purple">🧾 {t("checkin.payAtExit")}</div>
          ) : (
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-black text-gray-600">{t("checkin.totalDue")}</span>
              <span className="num font-mono text-lg font-black text-cosmic-purple">{money(total)} {t("currency")}</span>
            </div>
          )}
        </div>
      </div>

      {pass && !postpaid && <CouponPicker guardianId={guardian?.id ?? null} kidId={evalKidId} passProductId={pass.id} addons={addonList} disabled={free} value={coupon} onChange={setCoupon} />}

      {canRedeem && !postpaid && (
        <label className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-xs font-black text-amber-900">
          <input type="checkbox" className="rounded text-cosmic-purple focus:ring-0" checked={redeem} onChange={(e) => setRedeem(e.target.checked)} />
          🎁 {t("checkin.redeem", { name: kid?.name })}
        </label>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-64 flex-1">{!postpaid && <PaymentSection form={form} total={total} methods={[...methods]} />}</div>
        <div className="flex items-center gap-2">
          <select className="field !w-auto !py-1.5" value={waLocale} onChange={(e) => setWaLocale(e.target.value as "ar" | "en")} aria-label={t("checkin.whatsappLanguage")} disabled={!!guardian} title={t("checkin.whatsappLanguage")}>
            <option value="ar">💬 العربية</option>
            <option value="en">💬 English</option>
          </select>
          <button type="submit" className="btn-primary !px-5 !py-2.5 text-sm" disabled={!ready || busy}>{busy ? t("common.loading") : t("checkin.activate")}</button>
        </div>
      </div>
      {!shiftOpen && !postpaid && <p className="text-[11px] font-bold text-amber-700">⚠️ {t("shift.requiredHint")}</p>}
      {waiver && <WaiverDialog open guardianId={waiver.guardianId} guardianName={parent.trim()} kids={[{ id: waiver.kidId, name: kidName.trim() }]} onClose={() => setWaiver(null)} onSigned={() => void afterSigned()} />}
    </form>
  );
}
